import React from "react";
import {
  AccountTree,
  AddTask,
  CalendarMonth,
  Description,
  Groups,
  Insights,
  PersonAdd,
  WarningAmber,
} from "@mui/icons-material";
import { getTodayValue } from "../../../data/workspaceConfig";
import { workspaceProductSpec } from "../../../data/workspaceProductSpec";
import { WorkspaceErrorNotice } from "../common/WorkspaceErrorNotice";
import { WorkspaceInlineLoader } from "../common/WorkspaceInlineLoader";

const todayValue = getTodayValue();

const closedStatuses = ["Done", "Completed", "Archived", "Won", "Lost", "Paid", "Refunded", "Approved", "Rejected", "Cancelled", "Published", "Exported", "Ready"];
const activeProjectStatuses = ["Active", "Review", "Planned", "Planning", "In Progress", "Client Review", "Not Started"];

const isDone = (record) => closedStatuses.includes(record.status);
const isOverdue = (record) => record.due && record.due < todayValue && !isDone(record);
const byModule = (records, moduleName) => records.filter((record) => record.moduleName === moduleName);
const byStatus = (records, status) => records.filter((record) => record.status === status);
const percent = (part, total) => (total ? Math.round((part / total) * 100) : 0);
const specDetailKeys = [
  "items",
  "features",
  "unique",
  "quickActions",
  "advanced",
  "useCases",
  "statuses",
  "types",
  "pipeline",
  "roles",
  "conversions",
  "reports",
  "exampleOutputs",
  "includes",
];

const getSpecDetails = (item) =>
  specDetailKeys.flatMap((key) => (Array.isArray(item?.[key]) ? item[key] : []));

function SpecPanel({ eyebrow, title, items = [], columns = false }) {
  return (
    <section className="dashboard-spec-panel">
      <div className="functional-panel-header">
        <div>
          <span>{eyebrow}</span>
          <h3>{title}</h3>
        </div>
      </div>
      <div className={columns ? "dashboard-chip-grid" : "dashboard-bullet-list"}>
        {items.map((item, index) => {
          if (typeof item === "string") return <span key={item}>{item}</span>;

          const details = getSpecDetails(item);
          return (
            <article className="dashboard-spec-card" key={`${item.title || item.key || "spec"}-${index}`}>
              <strong>{item.title || item.key}</strong>
              {item.summary || item.bestFor ? <p>{item.summary || item.bestFor}</p> : null}
              {details.length ? (
                <div>
                  {details.map((detail) => (
                    <span key={detail}>{detail}</span>
                  ))}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function MetricTile({ label, value, note }) {
  return (
    <article>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}

function RecordList({ empty, records, actionLabel, onAction }) {
  if (!records.length) return <p className="empty-state">{empty}</p>;

  return (
    <div className="functional-list compact">
      {records.slice(0, 8).map((record) => (
        <article key={record.id}>
          <div>
            <strong>{record.title}</strong>
            <span>{record.moduleName} · {record.tab} · {record.owner || "Unassigned"}</span>
          </div>
          {actionLabel ? <button onClick={() => onAction?.(record)} type="button">{actionLabel}</button> : <em>{record.status}</em>}
        </article>
      ))}
    </div>
  );
}

function QuickActions({ onCreateRecord }) {
  const actions = [
    { label: "Create task", icon: AddTask, payload: { moduleName: "Work", tab: "Tasks", title: "Dashboard task", type: "Task", priority: "High", status: "To Do" } },
    { label: "Schedule meeting", icon: CalendarMonth, payload: { moduleName: "Schedule", tab: "Calendar", title: "Dashboard meeting", type: "meeting", priority: "Medium", status: "Planned" } },
    { label: "Add client", icon: Groups, payload: { moduleName: "Clients / CRM", tab: "Clients", title: "New client from dashboard", type: "client", priority: "Medium", status: "Active" } },
    { label: "Invite employee", icon: PersonAdd, payload: { moduleName: "People", tab: "Employees", title: "Employee invitation", type: "employee", priority: "Medium", status: "Planned" } },
    { label: "Create doc", icon: Description, payload: { moduleName: "Docs", tab: "All Docs", title: "Workspace document", type: "document", priority: "Low", status: "Planned" } },
  ];

  return (
    <div className="dashboard-action-grid">
      {actions.map((action) => {
        const Icon = action.icon;
        return (
          <button key={action.label} onClick={() => onCreateRecord?.({ ...action.payload, due: todayValue })} type="button">
            <Icon fontSize="small" />
            <span>{action.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function DashboardHero({ activeTab, liveNow, records }) {
  const healthPenalty = byStatus(records, "Blocked").length * 12 + records.filter(isOverdue).length * 10;
  const healthScore = Math.max(0, Math.min(100, 100 - healthPenalty));

  return (
    <section className="functional-hero">
      <div>
        <span>Dashboard · {activeTab}</span>
        <h2>Workspace command center</h2>
        <p>{workspaceProductSpec.product.positioning}</p>
      </div>
      <div className="live-sync-card">
        <span>Workspace health</span>
        <strong>{healthScore}%</strong>
        <small>Synced {liveNow.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</small>
      </div>
    </section>
  );
}

function DashboardOverview({ activity, onCreateRecord, onUpdateRecord, records }) {
  const dashboardModule = workspaceProductSpec.modules.find((module) => module.title === "Dashboard");
  const meetings = byModule(records, "Schedule").filter((record) => record.due === todayValue);
  const tasks = [...byModule(records, "Work"), ...byModule(records, "Tasks & Tickets"), ...byModule(records, "My Work")];
  const projects = byModule(records, "Projects");
  const people = byModule(records, "People");
  const clients = byModule(records, "Clients / CRM");
  const finance = byModule(records, "Finance");
  const docs = byModule(records, "Docs");
  const overdue = records.filter(isOverdue);
  const pendingTasks = tasks.filter((record) => !isDone(record));
  const activeProjects = projects.filter((record) => activeProjectStatuses.includes(record.status));
  const leaveRequests = people.filter((record) => record.tab === "Leaves" || /leave/i.test(record.title));

  const metricRows = [
    { label: "Today's meetings", value: meetings.length, note: "Schedule records due today" },
    { label: "Pending tasks", value: pendingTasks.length, note: "Tasks and tickets not done" },
    { label: "Active projects", value: activeProjects.length, note: "Projects currently moving" },
    { label: "Team attendance", value: people.length, note: "People records connected" },
    { label: "Leave requests", value: leaveRequests.length, note: "People leave queue" },
    { label: "Client updates", value: clients.length, note: "CRM records" },
    { label: "Revenue/credits", value: finance.length, note: "Finance records" },
    { label: "Recent activities", value: activity.length, note: "Workspace timeline" },
    { label: "Overdue tickets", value: overdue.length, note: "Past due and open" },
    { label: "Docs", value: docs.length, note: "Knowledge records" },
  ];

  return (
    <>
      <section className="functional-metrics dashboard-wide-metrics" aria-label="Dashboard overview metrics">
        {metricRows.map((metric) => <MetricTile key={metric.label} {...metric} />)}
      </section>

      <section className="functional-grid">
        <div className="functional-panel">
          <div className="functional-panel-header">
            <div>
              <span>Today's Focus</span>
              <h3>Delayed work and decisions</h3>
            </div>
          </div>
          <RecordList
            actionLabel="Open"
            empty="No overdue or review records yet. Create tasks, meetings, clients, docs, or projects to populate focus."
            onAction={(record) => onUpdateRecord?.(record.id, { status: record.status === "Review" ? "Done" : "Active" })}
            records={[...overdue, ...byStatus(records, "Review"), ...byStatus(records, "Blocked")]}
          />
        </div>
        <div className="functional-panel">
          <div className="functional-panel-header">
            <div>
              <span>Quick Actions</span>
              <h3>Create connected records</h3>
            </div>
          </div>
          <QuickActions onCreateRecord={onCreateRecord} />
        </div>
      </section>

      <section className="functional-grid">
        <div className="functional-panel">
          <div className="functional-panel-header">
            <div>
              <span>Recent Activity</span>
              <h3>Live workspace timeline</h3>
            </div>
          </div>
          <div className="activity-feed">
            {activity.slice(0, 8).map((item) => (
              <article key={item.id}>
                <span>{item.at}</span>
                <p>{item.message}</p>
              </article>
            ))}
            {!activity.length ? <p className="empty-state">No workspace activity yet. Backend changes will appear here.</p> : null}
          </div>
        </div>
        <SpecPanel eyebrow="Dashboard Module" title="Required overview functionality" items={dashboardModule ? [dashboardModule] : []} />
      </section>

      <section className="functional-grid">
        <SpecPanel eyebrow="Product Solution" title="Core capabilities covered" items={workspaceProductSpec.solutionCapabilities} columns />
        <SpecPanel eyebrow="Differentiators" title="What makes Workspace stronger" items={workspaceProductSpec.differentiators} />
      </section>
    </>
  );
}

function MyActivityDashboard({ currentUser, records }) {
  const name = currentUser?.name || currentUser?.user_name || "";
  const myRecords = name
    ? records.filter((record) => (record.owner || "").toLowerCase() === name.toLowerCase())
    : records.filter((record) => !record.owner);
  const tasks = myRecords.filter((record) => ["Work", "Tasks & Tickets", "My Work"].includes(record.moduleName));
  const meetings = myRecords.filter((record) => record.moduleName === "Schedule");
  const attendance = myRecords.filter((record) => record.moduleName === "People" && record.tab === "Attendance");
  const leaves = myRecords.filter((record) => /leave/i.test(record.tab) || /leave/i.test(record.title));
  const docs = myRecords.filter((record) => record.moduleName === "Docs");
  const rewards = myRecords.filter((record) => ["Finance", "Wallet"].includes(record.moduleName) || /reward|wallet|credit/i.test(record.title));

  return (
    <>
      <section className="functional-metrics">
        <MetricTile label="My tasks" value={tasks.length} note="Assigned task/ticket records" />
        <MetricTile label="My meetings" value={meetings.length} note="Schedule records" />
        <MetricTile label="My attendance" value={attendance.length} note="Tracker/attendance records" />
        <MetricTile label="My leaves" value={leaves.length} note="Leave and WFH requests" />
        <MetricTile label="My rewards" value={rewards.length} note="Wallet and incentive records" />
        <MetricTile label="My docs" value={docs.length} note="Documents connected to me" />
        <MetricTile label="Pending" value={myRecords.filter((record) => !isDone(record)).length} note="Open personal work" />
        <MetricTile label="Performance" value={`${percent(myRecords.filter(isDone).length, myRecords.length)}%`} note="Completed assigned records" />
      </section>
      <section className="functional-grid">
        <div className="functional-panel">
          <div className="functional-panel-header">
            <div>
              <span>Employee Dashboard</span>
              <h3>Personal work queue</h3>
            </div>
          </div>
          <RecordList empty="No assigned records from backend yet." records={myRecords} />
        </div>
        <SpecPanel eyebrow="Required Employee Widgets" title="From product document" items={workspaceProductSpec.roleDashboards.find((item) => item.title === "Employee Dashboard")?.items || []} />
      </section>
    </>
  );
}

function TeamActivityDashboard({ records }) {
  const owners = Array.from(new Set(records.map((record) => record.owner).filter(Boolean)));
  const workload = owners.map((owner) => {
    const owned = records.filter((record) => record.owner === owner);
    return {
      owner,
      total: owned.length,
      blocked: byStatus(owned, "Blocked").length,
      review: byStatus(owned, "Review").length,
      done: owned.filter(isDone).length,
    };
  });
  const trackerCards = workspaceProductSpec.advancedAdditions.find((item) => item.title === "Employee Work Tracker Integration")?.items || [];

  return (
    <>
      <section className="functional-metrics">
        <MetricTile label="Team members" value={owners.length} note="Owners found in backend records" />
        <MetricTile label="Blocked items" value={byStatus(records, "Blocked").length} note="Needs manager visibility" />
        <MetricTile label="Review items" value={byStatus(records, "Review").length} note="Waiting for decision" />
        <MetricTile label="Completion" value={`${percent(records.filter(isDone).length, records.length)}%`} note="Closed vs total records" />
      </section>
      <section className="functional-grid">
        <div className="functional-panel">
          <div className="functional-panel-header">
            <div>
              <span>Workload Heatmap</span>
              <h3>Owner load and blockers</h3>
            </div>
          </div>
          <div className="dashboard-heatmap">
            {workload.length ? workload.map((row) => (
              <article key={row.owner}>
                <strong>{row.owner}</strong>
                <span>{row.total} total</span>
                <em>{row.blocked} blocked</em>
                <small>{row.review} review · {row.done} done</small>
              </article>
            )) : <p className="empty-state">No owner data yet. Backend records will populate this heatmap.</p>}
          </div>
        </div>
        <SpecPanel eyebrow="Work Tracker Integration" title="Mac/Windows tracker dashboard cards" items={trackerCards} columns />
      </section>
    </>
  );
}

function ApprovalsDashboard({ onUpdateRecord, records }) {
  const approvals = records.filter((record) => record.status === "Review");
  const approvalEngine = workspaceProductSpec.advancedAdditions.find((item) => item.title === "Approval Engine")?.items || [];

  return (
    <>
      <section className="functional-metrics">
        <MetricTile label="Pending approvals" value={approvals.length} note="Records in Review" />
        <MetricTile label="Leave approvals" value={approvals.filter((record) => /leave/i.test(record.title)).length} note="Leave and WFH review" />
        <MetricTile label="Milestones" value={approvals.filter((record) => /milestone|project/i.test(record.title)).length} note="Project/client approval" />
        <MetricTile label="Finance approvals" value={approvals.filter((record) => /payment|payout|commission|invoice/i.test(record.title)).length} note="Payment and payout review" />
      </section>
      <section className="functional-grid">
        <div className="functional-panel">
          <div className="functional-panel-header">
            <div>
              <span>Approval Queue</span>
              <h3>Request -> Manager review -> Decision -> Audit log</h3>
            </div>
          </div>
          <RecordList
            actionLabel="Approve"
            empty="No approval records are in Review yet."
            onAction={(record) => onUpdateRecord?.(record.id, { status: "Done" })}
            records={approvals}
          />
        </div>
        <SpecPanel eyebrow="Approval Types" title="Required approval coverage" items={approvalEngine} />
      </section>
    </>
  );
}

function AnalyticsDashboard({ records }) {
  const overdue = records.filter(isOverdue);
  const blocked = byStatus(records, "Blocked");
  const done = records.filter(isDone);
  const healthScore = Math.max(0, 100 - blocked.length * 12 - overdue.length * 10);
  const intelligence = workspaceProductSpec.advancedAdditions.find((item) => item.title === "Workspace Intelligence Layer")?.items || [];

  return (
    <>
      <section className="functional-metrics">
        <MetricTile label="Workspace health" value={`${healthScore}%`} note="Blocked and overdue risk weighted" />
        <MetricTile label="Task completion" value={`${percent(done.length, records.length)}%`} note="Completed records" />
        <MetricTile label="Project delay risk" value={overdue.length} note="Past due open records" />
        <MetricTile label="Client risk" value={records.filter((record) => record.moduleName === "Clients / CRM" && record.status === "Blocked").length} note="Blocked CRM/client records" />
      </section>
      <section className="functional-grid">
        <SpecPanel eyebrow="Workspace Intelligence Layer" title="AI recommendations to implement" items={intelligence} columns />
        <SpecPanel eyebrow="Role-Based Dashboards" title="Smart dashboards by user type" items={workspaceProductSpec.roleDashboards} />
      </section>
      <section className="functional-grid">
        <SpecPanel eyebrow="Automations" title="Must-have automation families" items={Object.entries(workspaceProductSpec.automations).flatMap(([group, items]) => items.map((item) => `${group}: ${item}`))} />
        <SpecPanel eyebrow="Connected Platform Flows" title="Every module should talk to every other module" items={workspaceProductSpec.connectedFlows} />
      </section>
      <section className="functional-grid">
        <SpecPanel eyebrow="Premium Differentiators" title="Advanced additions from document" items={workspaceProductSpec.advancedAdditions} columns />
        <SpecPanel eyebrow="Relationship Models" title="Company relationship options" items={workspaceProductSpec.relationshipModels} />
      </section>
    </>
  );
}

export function DashboardFunctionalView({
  activeTab,
  activity,
  currentUser,
  error,
  isLoading,
  liveNow,
  onCreateRecord,
  onUpdateRecord,
  records,
}) {
  const renderSubtab = () => {
    if (activeTab === "My Activity") return <MyActivityDashboard currentUser={currentUser} records={records} />;
    if (activeTab === "Team Activity") return <TeamActivityDashboard records={records} />;
    if (activeTab === "Approvals") return <ApprovalsDashboard onUpdateRecord={onUpdateRecord} records={records} />;
    if (activeTab === "Analytics") return <AnalyticsDashboard records={records} />;
    return <DashboardOverview activity={activity} onCreateRecord={onCreateRecord} onUpdateRecord={onUpdateRecord} records={records} />;
  };

  return (
    <div className="dashboard-operational">
      <WorkspaceErrorNotice message={error} />
      {isLoading ? <p className="empty-state"><WorkspaceInlineLoader label="Loading workspace dashboard" /></p> : null}
      <DashboardHero activeTab={activeTab} liveNow={liveNow} records={records} />
      {renderSubtab()}
      <section className="functional-grid">
        <SpecPanel
          eyebrow="Roadmap Scope"
          title="MVP, Phase 2, and Phase 3 from product document"
          items={Object.entries(workspaceProductSpec.phases).flatMap(([phase, items]) => items.map((item) => `${phase}: ${item}`))}
        />
        <SpecPanel eyebrow="Suggested Entities" title="Backend entities to create with lww/lwa naming discipline" items={Object.entries(workspaceProductSpec.databaseEntities).flatMap(([group, items]) => items.map((item) => `${group}: ${item}`))} />
      </section>
      <section className="functional-grid">
        <SpecPanel eyebrow="Status Systems" title="Required workflow statuses" items={Object.entries(workspaceProductSpec.statusSystems).flatMap(([group, items]) => items.map((item) => `${group}: ${item}`))} />
        <SpecPanel eyebrow="Market Plans" title="Free, Plus, Organization plan coverage" items={workspaceProductSpec.plans} />
      </section>
      <section className="functional-grid">
        <SpecPanel
          eyebrow="Roles & Permissions"
          title="Access model from the document"
          items={[
            ...workspaceProductSpec.roles.map((role) => `Role: ${role}`),
            ...workspaceProductSpec.permissions.map((permission) => `Permission: ${permission}`),
            ...workspaceProductSpec.permissionModules.map((module) => `Permission module: ${module}`),
          ]}
        />
        <SpecPanel eyebrow="Product Vision" title={workspaceProductSpec.product.tagline} items={[workspaceProductSpec.product.vision, workspaceProductSpec.product.goal, workspaceProductSpec.product.usp]} />
      </section>
      <section className="functional-grid">
        <SpecPanel eyebrow="Target Audience" title="Primary users" items={workspaceProductSpec.audiences} />
        <SpecPanel eyebrow="Core Problem" title="Problems Workspace must solve" items={workspaceProductSpec.coreProblems} />
      </section>
      <section className="functional-grid">
        <SpecPanel eyebrow="Main Modules" title="Full module coverage" items={workspaceProductSpec.modules} columns />
        <SpecPanel eyebrow="Operating Discipline" title="Naming and data ownership" items={["Workspace web data uses lww- collections", "Mac and Windows tracker data uses lwa- collections", "Backend remains backend.lyriclious.com", "PWA remains workspace.lyriclious.com"]} />
      </section>
      <section className="dashboard-alert-strip">
        <WarningAmber fontSize="small" />
        <span>Tracker data must use lwa- collections. Workspace web data must use lww- collections.</span>
        <AccountTree fontSize="small" />
        <span>Dashboard currently reads lww backend records; tracker cards stay empty until lwa tracker ingestion is connected.</span>
        <Insights fontSize="small" />
        <span>All product-document items are now represented in dashboard scope panels.</span>
      </section>
    </div>
  );
}
